import React, { useState } from 'react';
import { Button, Form, Input, Modal, Row, Select, Space, Table } from 'antd';
import { observer } from 'mobx-react-lite';
import { useForm } from 'antd/es/form/Form';
import { toJS } from 'mobx';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import LoanLayout from '../LoanLayout';
import { useLoan } from '../../stores/store';
import Loading from '../Loading';
import ButtonNav from '../../components/ButtonNav/ButtonNav';
import { stepFinished } from '../../nav';
import PlaceholderLabel from '../../components/PlaceholderLabel/PlaceholderLabel';
import MoneyInput from '../../components/MoneyInput/MoneyInput';

import styles from './financial.module.scss';
import { formatCurrency } from '../../utils';
import { createSchemaValidator } from '../../validators/schemaValidator';
import { pickFields } from './meta';
import AppFormItem from '../components/AppFormItem';
import { Borrower, LoanPurpose, OtherAsset, OtherAssetName } from '../../schema';
import { ReactComponent as SelectArrowIcon } from '../../icons/select-arrow-icon.svg';
import { StepFooter } from '../components/StepFooter/StepFooter';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { FF_FULL_APPLICATION_STYLING } from '../../constants';

const formItemLayout = {
  labelCol: { span: 0 },
  wrapperCol: { span: 24 },
};

type Props = {};

let editingCol: number | null = null;

const schemaValidator = createSchemaValidator(pickFields('other-assets'));

const OtherAssetStep: React.FC<Props> = observer(function OtherAssetStep() {
  const loanStore = useLoan();
  const { loan } = loanStore;
  const [form] = useForm();
  const [showAdd, setShowAdd] = useState(false);
  const [modalAsset, setModalAsset] = useState<Partial<OtherAsset>>({});
  const isNewFunnel = useFeatureFlag({
    flag: FF_FULL_APPLICATION_STYLING,
  });

  if (!loan || loanStore.status === 'pending') {
    return <Loading />;
  }
  const borrower = loan.borrower as Borrower;
  const statement = borrower.financialStatement;
  if (!statement || !statement.otherAssets) {
    loanStore.setupOtherAssets(borrower);
    return null;
  }

  const handleSubmitAsset = () => {
    loanStore.addOtherAsset(borrower, modalAsset as OtherAsset, editingCol);
    // noinspection JSIgnoredPromiseFromCall
    loanStore.syncDoc();
    setShowAdd(false);
    form.resetFields();
    editingCol = null;
  };

  const handleCancel = () => {
    setShowAdd(false);
    form.resetFields();
    editingCol = null;
    return false;
  };

  const columns: ColumnsType<OtherAsset> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Percent',
      dataIndex: 'percentOwned',
      key: 'percentOwned',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (text: any) => (text ? formatCurrency(text) : ''),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (text: any) => (text ? formatCurrency(text) : ''),
    },
    {
      title: 'Payment',
      dataIndex: 'payment',
      key: 'payment',
      render: (text: any) => (text ? formatCurrency(text) : ''),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (text: any, record: OtherAsset, index: number) => (
        <Space size="middle">
          <Button
            onClick={() => {
              editingCol = index;
              form.setFieldsValue(record);
              setModalAsset({});
              setShowAdd(true);
            }}
          >
            <EditOutlined />
          </Button>
          <Button
            onClick={() => {
              loanStore.removeAsset(borrower, index);
            }}
          >
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <LoanLayout className="assetsLayout">
      {isNewFunnel && <div className="step-title">About You</div>}
      <h1>
        {loan?.purpose === LoanPurpose.boat
          ? 'Tell us about any other assets you own'
          : 'Other Assets'}
      </h1>
      {loan?.purpose === LoanPurpose.boat && <p>Add your other assets below (optional).</p>}

      {loan?.purpose !== LoanPurpose.boat && (
        <p className="assetsSubtitle">Please add other assets you may own.</p>
      )}
      <Table dataSource={toJS(statement.otherAssets)} columns={columns} pagination={false} />

      <Modal title="Add Other Asset" visible={showAdd} footer={false} onCancel={handleCancel}>
        <Form form={form} {...formItemLayout} onFinish={handleSubmitAsset}>
          <AppFormItem name="name" required rules={[schemaValidator('name', modalAsset)]}>
            <PlaceholderLabel label="Type of asset">
              <Select
                suffixIcon={<SelectArrowIcon />}
                showSearch={Object.keys(OtherAssetName).length > 7}
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {Object.entries(OtherAssetName).map(([key, label]) => (
                  <Select.Option key={key} value={label}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </PlaceholderLabel>
          </AppFormItem>
          <AppFormItem
            name="percentOwned"
            required
            rules={[
              schemaValidator('percentOwned', modalAsset, {
                message: 'Please enter a type of account or debt',
                transform: 'int',
              }),
            ]}
          >
            <PlaceholderLabel label="Percent Owned">
              <Input type="number" inputMode="numeric" min="0" max="100" />
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="value"
            required
            rules={[
              schemaValidator('value', modalAsset, {
                message: 'Please enter the value',
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Value">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="balance"
            rules={[
              schemaValidator('balance', modalAsset, {
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Remaining Balance (if applicable)">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <AppFormItem
            name="payment"
            rules={[
              schemaValidator('payment', modalAsset, {
                transform: 'float',
              }),
            ]}
          >
            <PlaceholderLabel label="Monthly Payment (if applicable)">
              <MoneyInput />
            </PlaceholderLabel>
          </AppFormItem>

          <Row justify="end">
            <Button name="cancel" type="default" htmlType="button" onClick={handleCancel}>
              Cancel
            </Button>

            <Button name="ok" className={styles.addButton} type="primary" htmlType="submit">
              Okay
            </Button>
          </Row>
        </Form>
      </Modal>

      <Row className={styles.tableActionRow}>
        <Button
          name="add"
          type="default"
          htmlType="button"
          onClick={() => {
            setShowAdd(true);
            return false;
          }}
        >
          <PlusOutlined />
          Add Asset
        </Button>
      </Row>

      <ButtonNav onNext={stepFinished} />
      <StepFooter type="secure" />
    </LoanLayout>
  );
});

export default OtherAssetStep;
